import { Controller } from "@hotwired/stimulus"

// Learner Matching block (TMS-808): the author's left/right pairing is the
// correct answer (data-matching-pair-id), the right column is shuffled on
// connect, and the learner clicks a left card then a right card to join them.
// Check marks every connected pair green/red and scores it; Try again clears the
// marks but keeps the connections, so only the wrong pairs need fixing; Reset
// clears everything and reshuffles. All state is client-side only — nothing is
// persisted.
//
// Connections are drawn as bezier paths into an SVG overlay sized to the board.
// Below STACKED_MAX_WIDTH the two columns stack vertically, where a line between
// them would cross the whole card, so the paths are dropped and each connected
// card carries a matching number badge instead.
const STACKED_MAX_WIDTH = 640
const SVG_NAMESPACE = "http://www.w3.org/2000/svg"

export default class extends Controller {
  static targets = [
    "board", "connectors", "rightList", "rightItem",
    "leftCard", "rightCard", "leftDot", "rightDot",
    "score", "checkButton", "resetButton", "completedBadge", "successBanner",
    "correctFeedback", "wrongFeedback"
  ]

  connect() {
    this.checked = false
    this.completed = false
    this.selectedLeftCard = null
    // left pair id -> right pair id. A pair id appears at most once on each side,
    // so the map is the whole connection state.
    this.connections = new Map()

    this.stackedQuery = window.matchMedia(`(max-width: ${STACKED_MAX_WIDTH}px)`)
    this.handleStackedChange = () => this.render()
    this.stackedQuery.addEventListener("change", this.handleStackedChange)

    this.resizeObserver = new ResizeObserver(() => this.drawConnections())
    this.resizeObserver.observe(this.boardTarget)

    this.shuffleRightItems()
    this.render()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.stackedQuery?.removeEventListener("change", this.handleStackedChange)
  }

  selectLeft(event) {
    event.preventDefault()
    if (this.completed) return

    const card = event.currentTarget
    const pairId = card.dataset.matchingPairId

    // A connected card is its own disconnect handle, so a second click on it
    // undoes the connection rather than starting a new selection.
    if (this.connections.has(pairId)) {
      this.connections.delete(pairId)
      this.selectedLeftCard = null
      this.clearFeedback()
      this.render()
      return
    }

    this.selectedLeftCard = this.selectedLeftCard === card ? null : card
    this.render()
  }

  selectRight(event) {
    event.preventDefault()
    if (this.completed) return

    const card = event.currentTarget
    const pairId = card.dataset.matchingPairId
    const connectedLeftId = this.leftIdConnectedTo(pairId)

    if (connectedLeftId) {
      this.connections.delete(connectedLeftId)
      this.selectedLeftCard = null
      this.clearFeedback()
      this.render()
      return
    }

    if (!this.selectedLeftCard) return

    this.connections.set(this.selectedLeftCard.dataset.matchingPairId, pairId)
    this.selectedLeftCard = null
    this.clearFeedback()
    this.render()
  }

  checkOrRetry(event) {
    event.preventDefault()

    if (this.checked) {
      // Try again keeps the connections so the learner only fixes what is wrong.
      this.clearFeedback()
      this.render()
    } else {
      this.check()
    }
  }

  check() {
    if (!this.allConnected) return

    const correctCount = this.correctConnectionCount()
    const total = this.leftCardTargets.length

    this.checked = true
    this.completed = correctCount === total
    this.selectedLeftCard = null
    this.element.classList.add("is-checked")
    // Unhidden before the text lands so the live region is in the accessibility
    // tree when the score arrives, otherwise the announcement is dropped.
    this.scoreTarget.hidden = false
    this.scoreTarget.textContent = `${correctCount} of ${total} correct${this.completed ? "!" : ""}`

    if (this.completed) {
      this.element.classList.add("is-completed")
      this.showCompletedCopy()
      this.checkButtonTarget.hidden = true
      this.completedBadgeTarget.hidden = false
    } else {
      this.showWrongCopy()
      this.checkButtonTarget.textContent = "Try again"
    }

    this.render()
  }

  reset(event) {
    event.preventDefault()
    this.connections.clear()
    this.selectedLeftCard = null
    this.clearFeedback()
    this.shuffleRightItems()
    this.render()
  }

  clearFeedback() {
    this.checked = false
    this.completed = false
    this.element.classList.remove("is-checked", "is-completed")
    this.successBannerTarget.hidden = true
    if (this.hasCorrectFeedbackTarget) this.correctFeedbackTarget.hidden = true
    if (this.hasWrongFeedbackTarget) this.wrongFeedbackTarget.hidden = true
    this.scoreTarget.hidden = true
    this.scoreTarget.textContent = ""
    this.checkButtonTarget.hidden = false
    this.checkButtonTarget.textContent = "Check Answer"
    this.completedBadgeTarget.hidden = true
  }

  // Authored If correct replaces the hardcoded banner; if the author left it
  // blank the existing "Every pair matched" copy stays.
  showCompletedCopy() {
    if (this.hasCorrectFeedbackTarget) {
      this.correctFeedbackTarget.hidden = false
      return
    }

    this.successBannerTarget.hidden = false
  }

  showWrongCopy() {
    if (this.hasWrongFeedbackTarget) this.wrongFeedbackTarget.hidden = false
  }

  // One pass that repaints every derived bit of the board from `connections`,
  // `selectedLeftCard` and `checked` — cheaper to reason about than patching
  // each card at every one of the eight entry points that can change them.
  render() {
    const badgeNumbers = this.badgeNumbers()

    this.leftCardTargets.forEach((card) => {
      const pairId = card.dataset.matchingPairId
      this.paintCard(card, {
        connected: this.connections.has(pairId),
        selected: card === this.selectedLeftCard,
        correct: this.checked && this.connections.get(pairId) === pairId,
        badge: badgeNumbers.get(pairId)
      })
    })

    this.rightCardTargets.forEach((card) => {
      const pairId = card.dataset.matchingPairId
      const connectedLeftId = this.leftIdConnectedTo(pairId)
      this.paintCard(card, {
        connected: connectedLeftId != null,
        selected: false,
        correct: this.checked && connectedLeftId === pairId,
        badge: connectedLeftId ? badgeNumbers.get(connectedLeftId) : undefined
      })
    })

    this.checkButtonTarget.disabled = !this.allConnected
    this.drawConnections()
  }

  paintCard(card, { connected, selected, correct, badge }) {
    const marked = this.checked && connected

    card.classList.toggle("is-connected", connected && !marked)
    card.classList.toggle("is-selected", selected)
    card.classList.toggle("is-correct", marked && correct)
    card.classList.toggle("is-wrong", marked && !correct)
    card.setAttribute("aria-pressed", String(connected || selected))

    const badgeElement = card.querySelector("[data-matching-card-badge]")
    if (badgeElement) badgeElement.textContent = badge == null ? "" : String(badge)

    const result = card.querySelector("[data-matching-card-result]")
    if (!result) return

    if (!marked) {
      result.textContent = connected ? "Connected" : ""
    } else {
      result.textContent = correct ? "Correct match" : "Wrong match"
    }
  }

  // Numbered in connection order so the stacked layout, which has no lines, still
  // shows which two cards belong together.
  badgeNumbers() {
    const numbers = new Map()
    Array.from(this.connections.keys()).forEach((leftId, index) => {
      numbers.set(leftId, index + 1)
    })

    return numbers
  }

  drawConnections() {
    const connectors = this.connectorsTarget
    connectors.replaceChildren()

    if (this.stacked) return

    const board = this.boardTarget.getBoundingClientRect()
    connectors.setAttribute("viewBox", `0 0 ${board.width} ${board.height}`)

    this.connections.forEach((rightId, leftId) => {
      const start = this.dotCentre(this.leftDotFor(leftId), board)
      const end = this.dotCentre(this.rightDotFor(rightId), board)
      if (!start || !end) return

      const path = document.createElementNS(SVG_NAMESPACE, "path")
      path.setAttribute("d", this.bezierPath(start, end))
      path.setAttribute("class", this.connectionClass(leftId, rightId))
      connectors.appendChild(path)
    })
  }

  connectionClass(leftId, rightId) {
    const classes = ["academy-matching-connector"]
    if (!this.checked) return classes.join(" ")

    classes.push(leftId === rightId ? "is-correct" : "is-wrong")

    return classes.join(" ")
  }

  // Control points pushed horizontally out from each dot, so the curve leaves and
  // arrives level with the card edge however far apart the two rows sit.
  bezierPath(start, end) {
    const curve = Math.max(Math.abs(end.x - start.x) / 2, 24)

    return `M ${start.x} ${start.y} C ${start.x + curve} ${start.y}, ${end.x - curve} ${end.y}, ${end.x} ${end.y}`
  }

  dotCentre(dot, board) {
    if (!dot) return null

    const box = dot.getBoundingClientRect()

    return { x: box.left + (box.width / 2) - board.left, y: box.top + (box.height / 2) - board.top }
  }

  leftDotFor(pairId) {
    return this.leftDotTargets.find((dot) => dot.closest("[data-matching-pair-id]")?.dataset.matchingPairId === pairId)
  }

  rightDotFor(pairId) {
    return this.rightDotTargets.find((dot) => dot.closest("[data-matching-pair-id]")?.dataset.matchingPairId === pairId)
  }

  leftIdConnectedTo(rightId) {
    for (const [leftId, connectedRightId] of this.connections) {
      if (connectedRightId === rightId) return leftId
    }

    return null
  }

  correctConnectionCount() {
    let count = 0
    this.connections.forEach((rightId, leftId) => {
      if (leftId === rightId) count += 1
    })

    return count
  }

  shuffleRightItems() {
    const items = this.rightItemTargets
    if (items.length < 2) return

    for (let index = items.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1))
      ;[items[index], items[swapIndex]] = [items[swapIndex], items[index]]
    }

    // Never present the already-solved order — a right column sitting level with
    // its left match gives the answer away. Rotating by one guarantees a
    // different permutation without reshuffling in a loop.
    const leftOrder = this.leftCardTargets.map((card) => card.dataset.matchingPairId)
    if (items.every((item, index) => this.pairIdOf(item) === leftOrder[index])) {
      items.push(items.shift())
    }

    items.forEach((item) => this.rightListTarget.appendChild(item))
  }

  pairIdOf(item) {
    return item.querySelector("[data-matching-pair-id]")?.dataset.matchingPairId
  }

  get allConnected() {
    return this.connections.size === this.leftCardTargets.length
  }

  get stacked() {
    return this.stackedQuery.matches
  }
}
